import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HeroSection.css';

// Import assets
import dancerBg from './assets/pic1-optimized.webp';
import dancerBgFallback from './assets/pic1-optimized.jpg';
import dancerBgMobile from './assets/pic1mobile.png';
import dancerNeon from './assets/dancer-neon.png';

/* ========== useBreakpoint Hook ========== */
const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState('desktop');

  useEffect(() => {
    let rafId;
    const checkBreakpoint = () => {
      if (rafId) return; // throttle to one per frame
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const width = window.innerWidth;
        if (width <= 480) setBreakpoint('mobile-sm');
        else if (width <= 768) setBreakpoint('mobile');
        else if (width <= 1024) setBreakpoint('tablet');
        else setBreakpoint('desktop');
      });
    };

    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint, { passive: true });
    return () => {
      window.removeEventListener('resize', checkBreakpoint);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return {
    breakpoint,
    isMobile: breakpoint === 'mobile' || breakpoint === 'mobile-sm',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
    isMobileSm: breakpoint === 'mobile-sm',
  };
};

const HeroSection = () => {
  const { isMobile, isMobileSm } = useBreakpoint();

  return (
    <section className={`hero-section ${isMobile ? 'hero-section--mobile' : ''}`}>
      {/* Mobile Gradient Overlays - rendered via CSS pseudo-elements */}
      
      {/* Headline - overlaid on mobile */}
      <div className="hero-headline-container">
        <motion.h1 
          className="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          Knowledge, made visible.
        </motion.h1>
        <motion.p 
          className="hero-subtext"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        >
          A platform to structure your intuition, craft and knowledge.
          For tutors, creators and every educator — built from your own materials, 
          lived experience and teaching flow.
        </motion.p>
      </div>

      {/* Visual Display - Dancer with Overlays */}
      <motion.div 
        className={`hero-visual-container ${isMobile ? 'hero-visual-container--mobile' : ''}`}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
      >
        <div className={`hero-visual-wrapper ${isMobile ? 'hero-visual-wrapper--mobile' : ''}`}>
          {/* Background Image - Mobile gets portrait-cropped version */}
          <picture>
            {isMobile && <source srcSet={dancerBgMobile} type="image/png" />}
            <source srcSet={dancerBg} type="image/webp" />
            <img
              src={dancerBgFallback}
              alt="Bharatanatyam dancer demonstrating traditional dance form"
              className={`hero-background-image ${isMobile ? 'hero-background-image--mobile' : ''}`}
            />
          </picture>

          {/* Dancer Neon Overlay */}
          <img
            src={dancerNeon}
            alt=""
            className={`dancer-neon-overlay ${isMobile ? 'dancer-neon-overlay--mobile' : ''}`}
            aria-hidden="true"
          />

          {/* Desktop: Overlays inside wrapper */}
          {!isMobile && (
            <>
              <StepListOverlay isMobile={false} isMobileSm={false} />
              <WaveformOverlay isMobile={false} />
              <EarlyAccessCTA isMobile={false} />
            </>
          )}

          {/* Mobile: Step list positioned independently on the image */}
          {isMobile && (
            <StepListOverlay isMobile={isMobile} isMobileSm={isMobileSm} />
          )}
        </div>

        {/* Mobile: Waveform and CTA at bottom */}
        {isMobile && (
          <div className="hero-overlays--mobile">
            <WaveformOverlay isMobile={isMobile} />
            <EarlyAccessCTA isMobile={isMobile} />
          </div>
        )}
      </motion.div>
    </section>
  );
};

/* ========== Early Access CTA Component ========== */
const EarlyAccessCTA = ({ isMobile }) => {
  const scrollToEarlyAccess = () => {
    const section = document.getElementById('early-access');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToMission = () => {
    const section = document.getElementById('mission-statement');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <motion.div
      className={`early-access-cta ${isMobile ? 'early-access-cta--mobile' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 1 }}
    >
      <p className="ea-cta-text">Get early access to Project Aana and help shape the platform.</p>
      <div className="ea-cta-buttons-row">
        <button className="ea-cta-button" onClick={scrollToEarlyAccess}>
          Register Interest
          <span className="ea-arrow">→</span>
        </button>
        {!isMobile && (
          <button className="ea-mission-link" onClick={scrollToMission}>
            Read Mission Statement
          </button>
        )}
      </div>
      {!isMobile && <span className="ea-micro-tagline">No credit card. No student accounts. No noise.</span>}
    </motion.div>
  );
};

/* ========== Step List Overlay Component ========== */
const StepListOverlay = ({ isMobile, isMobileSm }) => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { number: 1, title: 'Tatta Adavu', subtitle: 'Stamp + Samapadam' },
    { number: 2, title: 'Natta Adavu', subtitle: 'Araimandi + Triangular Hand Line' },
    { number: 3, title: 'Kuditta Mettu', subtitle: 'Hop + Heel Drop' },
    { number: 4, title: 'Teermanam', subtitle: 'Finishing Pose' },
  ];

  // Mobile: Show only 3 items
  const displaySteps = isMobile ? steps.slice(0, 3) : steps;

  // Auto-cycle through steps (slower on mobile for smoother feel)
  useEffect(() => {
    const cycleInterval = isMobile ? 3000 : 3200;
    const stepCount = isMobile ? displaySteps.length : steps.length;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % stepCount);
    }, cycleInterval);
    return () => clearInterval(interval);
  }, [steps.length, displaySteps.length, isMobile]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.5 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: isMobile ? 0 : 30, y: isMobile ? 10 : 0 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 },
    },
  };

  return (
    <motion.div
      className={`step-list-overlay ${isMobile ? 'step-list-overlay--mobile' : ''}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {displaySteps.map((step, index) => (
        <motion.div
          key={step.number}
          className={`step-item ${activeStep === index ? 'step-active' : ''} ${isMobile ? 'step-item--mobile' : ''}`}
          variants={itemVariants}
          onClick={() => setActiveStep(index)}
          whileHover={isMobile ? {} : { scale: 1.02, x: -4 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="step-number">
            <span>{step.number}</span>
            <AnimatePresence>
              {activeStep === index && (
                <motion.div
                  className="step-number-ring"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                />
              )}
            </AnimatePresence>
          </div>
          <div className="step-content">
            <span className="step-title">{step.title}</span>
            {!isMobile && <span className="step-subtitle">{step.subtitle}</span>}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

/* ========== Waveform Overlay Component ========== */
const WaveformOverlay = ({ isMobile }) => {
  const wavePath = useMemo(() => {
    const width = isMobile ? 280 : 400;
    const height = isMobile ? 30 : 40;
    const amplitude = isMobile ? 10 : 15;
    const frequency = 0.05;
    const points = [];
    
    for (let x = 0; x <= width; x += 2) {
      const y = height / 2 + Math.sin(x * frequency) * amplitude;
      points.push(`${x === 0 ? 'M' : 'L'} ${x} ${y}`);
    }
    
    return points.join(' ');
  }, [isMobile]);

  const beats = [
    { num: 1, symbol: '⟳' },
    { num: 2, symbol: '⟳' },
    { num: '', symbol: '' },
    { num: '', symbol: '' },
    { num: 1, symbol: '⟳' },
    { num: 2, symbol: '⟳' },
    { num: '', symbol: '' },
    { num: '', symbol: '' },
    { num: '', symbol: '' },
    { num: '', symbol: '' },
    { num: '', symbol: '' },
    { num: '', symbol: '' },
  ];

  const rhythmMarkers = ['3', '•', '6', '•', '-', '6-', '4', '•', '-', '13', '•', '14'];
  const counts = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  
  // Mobile: show fewer markers
  const displayRhythm = isMobile ? rhythmMarkers.slice(0, 8) : rhythmMarkers;
  const displayCounts = isMobile ? counts.slice(0, 8) : counts;

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, staggerChildren: 0.03, delayChildren: 0.8 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  return (
    <motion.div
      className={`waveform-overlay ${isMobile ? 'waveform-overlay--mobile' : ''}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Telenm Row - hidden on mobile */}
      {!isMobile && (
        <div className="waveform-row telenm-row">
          <span className="waveform-label">Telenm</span>
          <div className="waveform-markers">
            {beats.map((beat, i) => (
              <motion.div key={`beat-${i}`} className="beat-marker" variants={itemVariants}>
                <span className="beat-num">{beat.num}</span>
                <span className="beat-symbol">{beat.symbol}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Waveform SVG */}
      <div className="waveform-wave-container">
        <svg viewBox={isMobile ? "0 0 280 30" : "0 0 400 40"} className="waveform-svg" preserveAspectRatio="none">
          <defs>
            <filter id="waveGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          
          <motion.path
            d={wavePath}
            fill="none"
            stroke="rgba(51, 92, 255, 0.2)"
            strokeWidth={isMobile ? "3" : "4"}
            strokeLinecap="round"
          />
          
          <motion.path
            d={wavePath}
            fill="none"
            stroke="var(--landing-brand-blue)"
            strokeWidth={isMobile ? "2" : "2.5"}
            strokeLinecap="round"
            filter="url(#waveGlow)"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ pathLength: { duration: 1.5, ease: 'easeInOut' }, opacity: { duration: 0.3 } }}
          />
          
          <motion.circle
            r={isMobile ? "3" : "4"}
            fill="#FFFFFF"
            filter="url(#waveGlow)"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: isMobile ? 4 : 5, repeat: Infinity, ease: 'linear' }}
          >
            <animateMotion dur={isMobile ? "4s" : "5s"} repeatCount="indefinite" path={wavePath} />
          </motion.circle>
        </svg>
      </div>

      {/* Rhythm Row */}
      <div className="waveform-row rhythm-row">
        <span className="waveform-label">Rhythm</span>
        <div className="waveform-markers">
          {displayRhythm.map((marker, i) => (
            <motion.span
              key={`rhythm-${i}`}
              className={`rhythm-marker ${marker === '•' ? 'dot' : ''}`}
              variants={itemVariants}
            >
              {marker}
            </motion.span>
          ))}
        </div>
      </div>

      {/* Counts Row */}
      <div className="waveform-row counts-row">
        <span className="waveform-label">Counts</span>
        <div className="waveform-markers">
          {displayCounts.map((count, i) => (
            <motion.span key={`count-${i}`} className="count-marker" variants={itemVariants}>
              {count}
            </motion.span>
          ))}
        </div>
      </div>

      {/* Beat lines - hidden on mobile */}
      {!isMobile && (
        <div className="beat-lines">
          {counts.map((_, i) => (
            <motion.div
              key={`line-${i}`}
              className="beat-line"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ delay: 1 + i * 0.05, duration: 0.3 }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default HeroSection;
